import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { doc, getDoc, setDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../../firebaseFunctions/config";
import { useAuth } from "../../context/AuthContext";

const MAX_SHOW_COUNT = 3;

const ScrollIndicator = ({ targetRef, storageKey = "scrollHint", waitForOnboarding = true }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [onboardingComplete, setOnboardingComplete] = useState(false);
    const hasIncrementedRef = useRef(false);
    const { user } = useAuth();

    useEffect(() => {
        const checkOnboardingStatus = async () => {
            if (!user?.uid || !waitForOnboarding) {
                setOnboardingComplete(true);
                return;
            }

            try {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    const onboarding = userData.onboarding || {};
                    if (onboarding.hasSeenWelcome) {
                        setOnboardingComplete(true);
                    }
                }
            } catch (error) {
                console.error("Error checking onboarding status:", error);
                setOnboardingComplete(true);
            }
        };

        checkOnboardingStatus();
        
        const interval = setInterval(checkOnboardingStatus, 1000);
        return () => clearInterval(interval);
    }, [user?.uid, waitForOnboarding]);

    useEffect(() => {
        const checkAndShowHint = async () => {
            if (!user?.uid || !onboardingComplete) return;

            try {
                const hintDocRef = doc(db, "userHints", user.uid);
                const hintDoc = await getDoc(hintDocRef);

                if (hintDoc.exists()) {
                    const data = hintDoc.data();
                    const viewCount = data[storageKey] || 0;
                    
                    if (viewCount < MAX_SHOW_COUNT) {
                        setTimeout(() => setIsVisible(true), 500);
                    }
                } else {
                    await setDoc(hintDocRef, { [storageKey]: 0 });
                    setTimeout(() => setIsVisible(true), 500);
                }
            } catch (error) {
                console.error("Error checking scroll hint:", error);
                const localCount = parseInt(localStorage.getItem(`${storageKey}_${user.uid}`) || "0");
                if (localCount < MAX_SHOW_COUNT) {
                    setTimeout(() => setIsVisible(true), 500);
                }
            }
        };

        if (onboardingComplete) {
            checkAndShowHint();
        }
    }, [storageKey, user?.uid, onboardingComplete]);

    const incrementViewCount = useCallback(async () => {
        if (hasIncrementedRef.current || !user?.uid) return;
        hasIncrementedRef.current = true;

        try {
            const hintDocRef = doc(db, "userHints", user.uid);
            await updateDoc(hintDocRef, {
                [storageKey]: increment(1)
            });
        } catch (error) {
            console.error("Error updating scroll hint count:", error);
            const currentCount = parseInt(localStorage.getItem(`${storageKey}_${user.uid}`) || "0");
            localStorage.setItem(`${storageKey}_${user.uid}`, String(currentCount + 1));
        }
    }, [user?.uid, storageKey]);

    useEffect(() => {
        const handleScroll = () => {
            if (!hasScrolled) {
                setHasScrolled(true);
                setIsVisible(false);
                incrementViewCount();
            }
        };

        const target = targetRef?.current || window;
        target.addEventListener("scroll", handleScroll);
        
        return () => target.removeEventListener("scroll", handleScroll);
    }, [targetRef, hasScrolled, incrementViewCount]);

    useEffect(() => {
        if (isVisible) {
            const fadeTimer = setTimeout(() => {
                setIsVisible(false);
                incrementViewCount();
            }, 4000);
            return () => clearTimeout(fadeTimer);
        }
    }, [isVisible, incrementViewCount]);

    if (!isVisible || !onboardingComplete) return null;

    return (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-40 pointer-events-none">
            <div 
                className="flex flex-col items-center gap-2 animate-fade-in-up"
                style={{
                    animation: 'fadeInUp 0.5s ease-out, bounce 2s ease-in-out infinite 0.5s'
                }}
            >
                <div className="bg-white px-4 py-2 rounded-full shadow-lg border border-grey-100 flex items-center gap-2">
                    <span className="text-sm font-medium text-grey-600">
                        Scroll to see more
                    </span>
                    <ChevronDown className="w-4 h-4 text-grey-400 animate-bounce" />
                </div>
            </div>
            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default ScrollIndicator;
